/**
 * Code-owned product tour definitions.
 *
 * Tours are keyed by `LearningTour.key`, which the seed script sets to the tour's
 * full title (see `scripts/sandbox-setup/03_add_learning_tours.py:230`), so these
 * keys must match the database titles exactly.
 *
 * Targets are `test-id` attribute selectors rather than CSS paths, so restyling or
 * restructuring markup does not silently break a tour.
 *
 * Each tour lives in its own file under `tours/`; this module is the entry point.
 */

import { HOWTO_01 } from './tours/howto-01';
import { HOWTO_02 } from './tours/howto-02';
import { HOWTO_03 } from './tours/howto-03';
import { HOWTO_04 } from './tours/howto-04';
import { HOWTO_05 } from './tours/howto-05';
import { HOWTO_06 } from './tours/howto-06';
import { HOWTO_07 } from './tours/howto-07';
import { HOWTO_08 } from './tours/howto-08';
import { HOWTO_09 } from './tours/howto-09';
import { HOWTO_10 } from './tours/howto-10';
import { HOWTO_11 } from './tours/howto-11';
import { HOWTO_12 } from './tours/howto-12';
import { HOWTO_13 } from './tours/howto-13';
import { HOWTO_14 } from './tours/howto-14';
import { RISK_01 } from './tours/risk-01';
import { RISK_02 } from './tours/risk-02';
import { RISK_03 } from './tours/risk-03';
import { RISK_04 } from './tours/risk-04';
import { RISK_05 } from './tours/risk-05';
import { RISK_06 } from './tours/risk-06';
import { RISK_07 } from './tours/risk-07';
import { RISK_08 } from './tours/risk-08';
import { RISK_09 } from './tours/risk-09';
import { RISK_10 } from './tours/risk-10';
import { RISK_11 } from './tours/risk-11';
import { START_01 } from './tours/start-01';
import { START_02 } from './tours/start-02';
import { START_03 } from './tours/start-03';
import { START_04 } from './tours/start-04';
import { START_05 } from './tours/start-05';
import type { GgxTourDefinition } from './tours/tour-types';

export type {
    GgxTourDefinition,
    GgxTourStep,
    TourChapter,
    TourIcon,
    TourImage,
    TourIntro,
    TourOutro,
    TourStepButton,
    TourTab,
    TourVideo,
} from './tours/tour-types';

export const TOUR_REGISTRY: Readonly<Record<string, GgxTourDefinition>> = {
    [START_01.key]: START_01,
    [START_02.key]: START_02,
    [START_03.key]: START_03,
    [START_04.key]: START_04,
    [START_05.key]: START_05,
    [HOWTO_01.key]: HOWTO_01,
    [HOWTO_02.key]: HOWTO_02,
    [HOWTO_03.key]: HOWTO_03,
    [HOWTO_04.key]: HOWTO_04,
    [HOWTO_05.key]: HOWTO_05,
    [HOWTO_06.key]: HOWTO_06,
    [HOWTO_07.key]: HOWTO_07,
    [HOWTO_08.key]: HOWTO_08,
    [HOWTO_09.key]: HOWTO_09,
    [HOWTO_10.key]: HOWTO_10,
    [HOWTO_11.key]: HOWTO_11,
    [HOWTO_12.key]: HOWTO_12,
    [HOWTO_13.key]: HOWTO_13,
    [HOWTO_14.key]: HOWTO_14,
    [RISK_01.key]: RISK_01,
    [RISK_02.key]: RISK_02,
    [RISK_03.key]: RISK_03,
    [RISK_04.key]: RISK_04,
    [RISK_05.key]: RISK_05,
    [RISK_06.key]: RISK_06,
    [RISK_07.key]: RISK_07,
    [RISK_08.key]: RISK_08,
    [RISK_09.key]: RISK_09,
    [RISK_10.key]: RISK_10,
    [RISK_11.key]: RISK_11,
};

export function getTourDefinition(key: string): GgxTourDefinition | undefined {
    return TOUR_REGISTRY[key];
}
